import { useAuth0 } from "@auth0/auth0-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { consoleDebug } from "src/utils"
import { AuthContext } from "./AuthContext"

const AUTH0_AUDIENCE = "mbls-global-api"

export const Auth0AuthProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { isAuthenticated, isLoading, loginWithRedirect, user, getAccessTokenSilently } = useAuth0()

  // Expose Auth0 token getter globally for non-React code (baseQuerySl2.ts).
  // Set synchronously during render so it's available before any child API calls.
  // Wraps getAccessTokenSilently to recover from consent_required / login_required
  // by escalating to an interactive redirect — silent auth alone cannot resolve these.
  window.Auth0 = {
    getAccessTokenSilently: async () => {
      try {
        return await getAccessTokenSilently()
      } catch (e: any) {
        const code = e?.error
        if (code === "consent_required" || code === "login_required") {
          await loginWithRedirect({
            authorizationParams: { audience: AUTH0_AUDIENCE },
            appState: { returnTo: window.location.pathname + window.location.search },
          })
          // loginWithRedirect navigates away; this promise effectively never resolves.
          return ""
        }
        throw e
      }
    },
  }

  useEffect(() => {
    return () => {
      delete (window as any).Auth0
    }
  }, [])

  const [isProcessing, setIsProcessing] = useState(false)
  const [isProcessed, setIsProcessed] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading) return

    if (isAuthenticated && !isProcessing) {
      if (
        ["/login", "", "/", window.baseName].includes(window.location.pathname)
      ) {
        navigate("/us/profile")
      }
    }

    setIsProcessed(true)
    setIsProcessing(false)
  }, [isAuthenticated, isLoading, isProcessing, navigate])

  const ctx = {
    isAuthenticated,
    isProcessing,
    isProcessed,
    isReady: !isLoading && isProcessed && !isProcessing,
  }

  consoleDebug("Auth0 AuthContext", ctx)
  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>
}
